import type { ReactElement } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import { useFormState } from 'react-final-form'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { WizardForm } from '@/components/composed/forms'
import { WizardFormPage } from '@/components/composed/forms/WizardForm'
import { BubblingTooltip, Button, ErrorMessage, InfoText, PaginationDots } from '@/components/ui'
import {
  Checkbox,
  DatePicker,
  LoanPurposeSelect,
  PolicyAgreement,
  RegionSelect,
  SearchCitiesWithSuggestions,
  TextInput,
} from '@/components/ui/fields'
import { MultiRegionSelect } from '@/components/ui/fields/MultiRegionSelect/MultiRegionSelect'
import { formatHighlightedText } from '@/components/ui/HighlightedText'
import { setInitialStatus } from '@/logic/requestStatus/actions'
import type { IRequestState } from '@/logic/requestStatus/interfaces'
import { getRequestMeta, getRequestState } from '@/logic/requestStatus/reducer'
import type { RootState } from '@/redux/interfaces'
import { useMount } from '@/utils/hooks'
import type {
  ICustomizedFormFromRequestState,
  ICustomizedFormProps,
  ICustomizedFormStepProps,
  TCustomizedFormFieldType,
} from '../interfaces'
import { messages } from './messages'
import './styles.scss'

const Field: Record<
  TCustomizedFormFieldType,
  | typeof TextInput
  | typeof DatePicker
  | typeof Checkbox
  | typeof RegionSelect
  | typeof MultiRegionSelect
  | typeof PolicyAgreement
  | typeof SearchCitiesWithSuggestions
  | typeof LoanPurposeSelect
  | typeof InfoText
> = {
  text: TextInput,
  datePicker: DatePicker,
  checkbox: Checkbox,
  policyAgreement: PolicyAgreement,
  regionDropdown: RegionSelect,
  regionMultiDropdown: MultiRegionSelect,
  searchCitiesWithSuggestions: SearchCitiesWithSuggestions,
  loanPurposeSelect: LoanPurposeSelect,
  infoText: InfoText,
}

const loanAmountValidate = (state: string) => (value: string) => (Number(value) === Number(state) ? 'incorrectValue' : undefined)

const CustomizedFormStep: React.FC<ICustomizedFormStepProps & ICustomizedFormFromRequestState> = props => {
  const {
    fields,
    title,
    titleId,
    description,
    submitButton,
    errorMessageId,
    requestState,
    buttons,
    showPreviousForm,
    pageNumber,
    backButton,
    totalPagesQuantity,
    shouldShowRequiringFieldsLabel = true,
    hasValidationErrors,
    validating,
    tooltipText,
    tooltipTextId,
    shouldDisableButton,
    ignoreValidationForSubmitButton,
    requestStatusMeta,
    isMobile,
    calcButton,
  } = props

  const { isLoading, isFailure } = requestState
  const [loanValue, setLoanValue] = useState('')
  const { values } = useFormState()
  const { loanAmount } = values
  const { productId } = values

  useEffect(() => {
    setLoanValue(loanAmount?.value)
  }, [loanAmount])

  const isSubmitButtonDisabled = shouldDisableButton || (ignoreValidationForSubmitButton ? false : hasValidationErrors || validating)

  const submitFormButton = (
    <Button
      type="submit"
      modifiers={['full-width', 'green']}
      loaderProps={{ isVisible: isLoading }}
      isDisabled={isSubmitButtonDisabled}
      {...submitButton}
      animatedContainerClass={shouldDisableButton ? '' : submitButton?.animatedContainerClass}
    />
  )

  return (
    <section className="customized-form">
      {(titleId || title) && <h3 className="space-holder24 customized-form__title">{title ?? <FormattedMessage id={titleId} />}</h3>}
      {description && <p className="customized-form__description">{description}</p>}
      {fields.map(({ fieldType = 'text', ...field }) => {
        const Component = Field[fieldType]

        const fieldProps =
          isFailure && field.shouldBeTouchedBeforeError
            ? {
                ...field,
                shouldBeTouchedBeforeError: false,
                customValidator: loanAmountValidate(loanValue),
                errorText: requestStatusMeta,
              }
            : field

        //@ts-ignore
        return <Component key={fieldProps.name} {...fieldProps} className={classNames('space-holder8', fieldProps.className)} />
      })}
      {shouldShowRequiringFieldsLabel && productId !== 'AGENT' && (
        <p className="customized-form__requiring-fields-sign text_super-small-from-sm color_pale-black space-holder-top16">
          {messages.required}
        </p>
      )}
      <ErrorMessage
        isVisible={isFailure && Boolean(errorMessageId)}
        textId={requestStatusMeta || errorMessageId}
        className="customized-form__error text_center space-holder-top24"
      />
      <div className={productId === 'AGENT' ? 'customized-form__custom-actions' : 'customized-form__actions'}>
        {backButton && !isMobile && <Button {...backButton} onClick={backButton.onClick ? backButton.onClick : showPreviousForm} />}
        {calcButton && <Button {...calcButton} />}
        {(tooltipTextId || tooltipText) && shouldDisableButton ? (
          <BubblingTooltip messageId={tooltipTextId} content={tooltipText}>
            <span className={classNames('customized-form__submit-button-wrapper', submitButton?.animatedContainerClass)}>
              {submitFormButton}
            </span>
          </BubblingTooltip>
        ) : (
          submitFormButton
        )}
      </div>
      {backButton && isMobile && <Button {...backButton} onClick={backButton.onClick ? backButton.onClick : showPreviousForm} />}
      {buttons?.map((buttonProps, index) => (
        <Button
          className="space-holder-top8"
          key={`${buttonProps.textId}_${index}`}
          loaderProps={{ isVisible: isLoading }}
          {...buttonProps}
        />
      ))}
      {productId === 'AGENT' && <div className="customized-form__terms">{formatHighlightedText(messages.agreement)}</div>}
      {typeof pageNumber === 'number' && typeof totalPagesQuantity === 'number' && totalPagesQuantity >= 2 && (
        <PaginationDots dotsQuantity={totalPagesQuantity} activeDotNumber={pageNumber + 1} className="space-holder-top32" />
      )}
    </section>
  )
}

const mapState = (requestStatusName: string) => (state: RootState) => ({
  requestState: getRequestState(requestStatusName)(state),
  requestStatusMeta: getRequestMeta(requestStatusName)(state),
})

function CustomizedForm<FormValues>(props: ICustomizedFormProps<FormValues>): ReactElement {
  const {
    steps,
    onSubmit,
    initialValues,
    requestStatusName,
    tooltipTextId,
    tooltipText,
    shouldDisableButton,
    ignoreValidationForSubmitButton,
    autoFocus,
    forceScrollToTop,
    shouldShowRequiringFieldsLabel,
    shouldUseLocalRequestState,
  } = props

  const [localRequestState, setLocalRequestState] = useState<IRequestState>({
    isFailure: false,
    isLoaded: false,
    isLoading: false,
  })
  const { requestState, requestStatusMeta } = useSelector(mapState(requestStatusName))
  const dispatch = useDispatch()
  const requestMessage = typeof requestStatusMeta === 'object' ? '' : requestStatusMeta

  const clearRequestState = () => {
    if (requestStatusName) dispatch(setInitialStatus({ name: requestStatusName }))
  }

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setLocalRequestState({
        isFailure: false,
        isLoaded: false,
        isLoading: true,
      })

      try {
        await onSubmit(values)

        setLocalRequestState({
          isFailure: false,
          isLoaded: true,
          isLoading: false,
        })
      } catch (error) {
        setLocalRequestState({
          isFailure: true,
          isLoaded: false,
          isLoading: false,
        })

        throw error
      }
    },
    [onSubmit],
  )

  useMount(clearRequestState, clearRequestState)

  const requestStateToUse = shouldUseLocalRequestState ? localRequestState : requestState

  return (
    <WizardForm<FormValues> onSubmit={handleSubmit} initialValues={initialValues} autoFocus={autoFocus} forceScrollToTop={forceScrollToTop}>
      {steps.map((stepProps, index) => (
        <WizardFormPage<ICustomizedFormStepProps & ICustomizedFormFromRequestState, FormValues>
          {...stepProps}
          key={`${stepProps.title ?? stepProps.titleId}_${index}`}
          Component={CustomizedFormStep}
          requestState={requestStateToUse}
          requestStatusMeta={requestMessage}
          tooltipTextId={tooltipTextId}
          tooltipText={tooltipText}
          shouldDisableButton={shouldDisableButton}
          ignoreValidationForSubmitButton={ignoreValidationForSubmitButton}
          shouldShowRequiringFieldsLabel={shouldShowRequiringFieldsLabel}
        />
      ))}
    </WizardForm>
  )
}

export default CustomizedForm
